import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLACEMENT = "HOMEPAGE_EMPLOYER_HIRING_BANNER";
const CREATIVE_URL = "/ads/employer-hiring-banner.webp";

/**
 * Native (first-party) employer-conversion banner on the public homepage.
 * NOT sponsored/paid — no "Sponsored" label. Tracks impressions + clicks
 * to /api/public/native-banners/events so analytics stay separate from paid ads.
 */
export default function EmployerConversionBanner() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const imprSent = useRef(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const audience = loading ? "guest"
    : !user ? "guest"
    : user.role === "client" ? "client"
    : user.role === "candidate" ? "candidate"
    : "other";

  // Fire a single impression once the image has loaded (real render).
  useEffect(() => {
    if (!imgLoaded || imprSent.current) return;
    imprSent.current = true;
    api.post("/public/native-banners/events", {
      placement: PLACEMENT,
      event: "impression",
      audience,
    }).catch(() => {});
  }, [imgLoaded, audience]);

  const handleClick = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/native-banners/events", {
        placement: PLACEMENT,
        event: "click",
        audience,
      });
    } catch { /* non-blocking */ }
    // Authenticated employer → straight to the Job Builder.
    if (user && user.role === "client") {
      navigate("/employer/job-builder");
    } else {
      navigate("/employer-portal/signup");
    }
  };

  return (
    <section className="max-w-6xl mx-auto px-6 pt-10 md:pt-14" data-testid="employer-conversion-banner-section">
      <a
        href="/employer-portal/signup"
        onClick={handleClick}
        data-testid="employer-conversion-banner"
        aria-label="Hiring? Post a job on ConnectMyHub"
        className="group block rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 transition-shadow bg-white"
      >
        <img
          src={CREATIVE_URL}
          alt="Hiring? Post your open role on ConnectMyHub and get connected to vetted talent worldwide."
          onLoad={() => setImgLoaded(true)}
          loading="eager"
          decoding="async"
          data-testid="employer-conversion-banner-img"
          className="w-full h-auto block transition-transform duration-500 ease-out group-hover:scale-[1.01]"
        />
      </a>
    </section>
  );
}
